import { cn } from '~/lib/utils';
import { ActivityGlyph } from './ActivityGlyph';
import styles from './ActivityListRow.module.css';
import type { ChatTheme, ItemState } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ActivityListRowProps {
  /**
   * The three-state ladder, and the only thing that colours the row:
   * `done` = muted mark + muted label · `running` = **subtle** mark + muted label ·
   * `pending` = hint mark + **hint** label.
   *
   * Note the asymmetry, which is deliberate and drawn that way in *Outline build* (the one
   * artboard that shows all three at once): a running row brightens only its *mark*, because the
   * work is not finished and the name of the thing has not changed. A pending row dims both,
   * because it has not started.
   */
  state: ItemState;
  /**
   * A 500-weight run before the label, held at `--cv15-text-subtle` in every row state.
   *
   * Linear issue keys only (*Linear*, *Fetching context*) — the one two-run row in the whole
   * system. The keys are meant to form a readable column down the left of the card, which is why
   * the weight and the tone are pinned rather than following the row.
   */
  itemKey?: string;
  /** The row itself — "Cover", "Checkout flow redesign", "Writing artifacts · not started". */
  label: string;
  /**
   * One nesting level, and only one. *Outline build* is the single artboard that nests: left inset
   * goes
   * 12px -> 32px, right inset stays at 12px, block padding unchanged — a 20px step.
   */
  indent?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/** What the mark means, for the readers who cannot see it. */
const STATE_WORD: Record<ItemState, string> = {
  done: 'Done',
  running: 'Running',
  pending: 'Not started',
};

/**
 * One line inside an `ActivityList` — a sub-step, an artifact, an issue, an edit.
 *
 * Five agents proposed it (`AgentListRow`, `OutlineRow`, `ToolListRow`, `AgentArtifactListItem`,
 * `StepRow(listed)`) and all five drew the same 32px box. It is not the same object as
 * `ActivityRow`: that one is a 20px *header* with a spacer and a chevron; this one is a 32px
 * *member* with neither, and its icon→label gap is 12px rather than 8. The two numbers are what
 * separate a line that heads a card from a line that sits inside one.
 *
 * A row is never clickable in any artboard, so it stays a `<li>` and not a button.
 */
export function ActivityListRow({
  state,
  itemKey,
  label,
  indent = false,
  theme: themeProp,
  className,
}: ActivityListRowProps) {
  const theme = useV15Theme(themeProp);
  return (
    <li
      className={cn(
        chatThemeClass(theme),
        styles.root,
        styles[state],
        indent && styles.indent,
        className
      )}
    >
      {/* 16px on every row on every artboard — the list has no size ladder, only a tone one. */}
      <ActivityGlyph state={state} size={16} theme={theme} />
      {/* The mark is `aria-hidden`, so without this a pending row and a done row read as the same
          sentence. It rides in the `<li>`'s own text, inside the list's `aria-live` region, which
          is what makes "Pipeline" turning from Not started to Running audible. */}
      <span className={styles.stateWord}>{STATE_WORD[state]}</span>
      {itemKey ? <span className={styles.itemKey}>{itemKey}</span> : null}
      <span className={styles.label}>{label}</span>
    </li>
  );
}
